import { createAction, createReducer } from "@reduxjs/toolkit"
import dayjs from "dayjs"
import customParseFormat from "dayjs/plugin/customParseFormat"
import { api } from "./helpers/api"
import { LOADING_INITIAL_STATE, setFailed, setReceived, setRequested } from "modules/loader-watchers/helpers/loading"
import { toSearchParams } from "helpers/form"
import { buildGuestTypes } from "pagesV2/pro/Bookings/CheckIn/checkInUtils"

dayjs.extend(customParseFormat)

// Projected auto-collect schedule = 24h before the experience start. Mirrors the old booking
// detail page (PaymentSummary), which derives this on the FE rather than from the backend, so
// the date is available even when auto-collect is currently off (the enable-confirmation modal
// needs it). booked_datetime is "MMMM DD, YYYY", start_time is a "h:mm A"-style clock string.
const buildCollectionSchedule = (bookedDatetime, startTime) => {
  if (!bookedDatetime || !startTime) return null
  const start = dayjs(`${bookedDatetime} ${startTime}`, ["MMMM D, YYYY h:mm A", "MMMM DD, YYYY h:mm A", "MMMM D, YYYY hh:mm A"])
  return start.isValid() ? start.subtract(24, "hours").format("MMM D, YYYY - hh:mm A") : null
}

const PAGINATION_INITIAL_STATE = {
  page: 1,
  next_page: null,
  prev_page: null,
  total_results: 0,
  total_pages: 1
}

// ACTIONS
const requested = createAction("reservations/requested")
const received = createAction("reservations/received")
const failed = createAction("reservations/failed")

const requestedDetail = createAction("reservations/detail/requested")
const receivedDetail = createAction("reservations/detail/received")
const failedDetail = createAction("reservations/detail/failed")

const requestedCheckIn = createAction("reservations/checkIn/requested")
const receivedCheckIn = createAction("reservations/checkIn/received")
const failedCheckIn = createAction("reservations/checkIn/failed")

// Lazy-loaded detail sub-resources (see RESERVATION_DETAIL_API_SPLIT.md)
const requestedWaivers = createAction("reservations/waivers/requested")
const receivedWaivers = createAction("reservations/waivers/received")
const failedWaivers = createAction("reservations/waivers/failed")

const requestedPaymentHistory = createAction("reservations/paymentHistory/requested")
const receivedPaymentHistory = createAction("reservations/paymentHistory/received")
const failedPaymentHistory = createAction("reservations/paymentHistory/failed")

const requestedCommunications = createAction("reservations/communications/requested")
const receivedCommunications = createAction("reservations/communications/received")
const failedCommunications = createAction("reservations/communications/failed")

const requestedAnswers = createAction("reservations/answers/requested")
const receivedAnswers = createAction("reservations/answers/received")
const failedAnswers = createAction("reservations/answers/failed")

const requestedPaymentOption = createAction("reservations/paymentOption/requested")
const receivedPaymentOption = createAction("reservations/paymentOption/received")
const failedPaymentOption = createAction("reservations/paymentOption/failed")

// MAPPER — backend snake_case → FE camelCase internal shape
export const mapReservationItem = (item) => {
  const isMultiDay = item.duration_type === "multi_day"
  const experienceTime = isMultiDay
    ? item.experience_time?.start_time || null
    : [item.experience_time?.start_time, item.experience_time?.end_time].filter(Boolean).join(" - ") || null

  return {
    id: item.uuid,
    bookingId: item.booking_id,
    name: item.full_name || "",
    phone: item.phone || null,
    email: item.email || null,
    // Client identity for the messaging modal (ClientContactForm sends by numeric user_id)
    userId: item.user_id ?? null,
    user: item.user || null,
    // Lifecycle status for the All Reservations sub-tabs (booked/requested/completed/canceled)
    status: item.status || null,
    experience: item.trip_title || "",
    experienceDate: item.experience_date || "",
    experienceTime: experienceTime,
    experienceIsGroup: item.experience_is_group ?? false,
    guestCount: item.total_guest_count || 0,
    checkInState: item.check_in_state || "pending",
    // Partial check-in/out: how many of total_guest_count are checked in / out
    checkedInCount: item.checked_in_count ?? 0,
    checkedOutCount: item.checked_out_count ?? 0,
    // Per-type breakdown for the check-in/out modals (falls back to a single "Guests" row)
    guestTypes: buildGuestTypes(item.guest_types, item.total_guest_count || 0, item.checked_in_count ?? 0, item.checked_out_count ?? 0),
    // Required documents all signed → check-in without the incomplete-docs override
    waiversComplete: (item.docs_signed ?? 0) >= (item.docs_total ?? 0),
    paymentTag: item.payment_tag || { label: "Payment Failed", color: "red" },
    collectionTag: item.collection_tag || null,
    docsSign: item.docs_signed ?? 0,
    docsTotal: item.docs_total ?? 0
  }
}

const titleCase = (value) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : value)

// MAPPER — backend reservation detail (flat booking) → ReservationDetailPage shape.
// The detail page components expect the nested shape below; the API returns a flat
// snake_case booking with client/trip/payment_summary/payment_history/booking_waivers.
export const mapReservationDetail = (payload) => {
  const item = payload?.data || payload
  if (!item) return null

  const client = item.client || {}
  const trip = item.trip || {}
  const paymentSummary = item.payment_summary || {}
  const waivers = Array.isArray(item.booking_waivers) ? item.booking_waivers : []
  const paymentHistory = Array.isArray(item.payment_history) ? item.payment_history : []

  const clientName = [client.first_name, client.last_name].filter(Boolean).join(" ").trim()
  const signedWaiverCount = waivers.filter((waiver) => waiver.is_signed).length
  const remainingBalance = paymentSummary.remaining_balance ?? item.price_breakdown?.remaining_balance ?? 0
  const isPaidInFull = remainingBalance === 0

  // Prefer the same payment_tag / collection_tag the list endpoint returns so the
  // detail panel stays consistent with the listing/ActionQueue. Fall back to a basic
  // paid/balance-due tag until the backend includes them on the detail payload.
  const paymentTag = item.payment_tag || { label: isPaidInFull ? "Paid in Full" : "Balance Due", color: isPaidInFull ? "green" : "orange" }
  const collectionTag = item.collection_tag || null

  const hasDeposit = (item.deposit_amount ?? 0) > 0
  const isCashBalance = /cash/i.test(paymentTag.label || "")
  const depositLabel = (() => {
    if (isPaidInFull) return "Total payment collected"
    if (!hasDeposit) return "No deposit collected, balance due"
    return isCashBalance ? "Deposit paid & balance due in cash" : "Deposit paid & balance due to card"
  })()

  // Per-guest-type breakdown rows; only include a type when its count is > 0.
  // NOTE: payment_summary.{type}.price is already the LINE TOTAL for that guest type
  // (e.g. adult.price 40 + child.price 90 = subtotal 130) — do NOT multiply by count.
  // Flat-rate bookings instead show a single "Flat Rate" base line plus any extra-guest lines,
  // matching the old booking-details PaymentSummary (useSummaryDetails charge_flat_rate branch).
  const buildGuestLine = (type, block = {}) => {
    const count = block.count ?? 0
    const price = block.price ?? 0
    return { type, count, price, amount: price }
  }
  const guestLines =
    paymentSummary.pricing_type === "charge_flat_rate"
      ? [
          { type: "flat_rate", count: 0, price: paymentSummary.flat_rate ?? 0, amount: paymentSummary.flat_rate ?? 0 },
          ...["extra_adult", "extra_child", "extra_infant", "extra_passenger"]
            .map((type) => buildGuestLine(type, paymentSummary[type]))
            .filter((line) => line.count > 0)
        ]
      : ["adult", "child", "infant", "passenger"]
          .map((type) => buildGuestLine(type, paymentSummary[type]))
          .filter((line) => line.count > 0)

  const discountAmount = Number(item.discount_amount ?? 0)
  const tipAmount = Number(item.tip_amount ?? 0)

  // Adjusted total when "Adjust Booking Price" has been used; the top-level total_amount
  // can lag behind, so prefer the summary's computed total.
  const totalPrice = paymentSummary.total_price ?? paymentSummary.new_adjusted_price ?? item.total_amount ?? 0

  // Price adjustments made via "Adjust Booking Price"; shown as their own lines so the
  // total (and any resulting overpayment/refund) is explained. price_difference is a
  // string in the payload, and no-op ($0) entries are filtered out.
  const priceAdjustments = (paymentSummary.adjust_price_history || [])
    .map((adjustment, index) => ({
      id: adjustment.id ?? `adjustment-${index}`,
      amount: Number(adjustment.price_difference) || 0,
      reason: adjustment.reason || null
    }))
    .filter((adjustment) => adjustment.amount !== 0)

  // Single combined "Taxes & Fees" row — every fee rolled into one total, with an itemised
  // breakdown for the expandable panel. Mirrors the old booking-details PaymentSummary exactly
  // (useSummaryDetails mergeAllFeesToTax + info): the card processing fee is sourced ONLY from the
  // custom_fees "Card Processing Fee" entry, which the backend injects only when the fee is enabled
  // / passed through to the guest — so it shows and adds only in that case (we deliberately do NOT
  // read the flat card_processing_fee field, which can be present even when the guide absorbs it).
  // tax_and_fee is the platform fee; only add it when custom_fees doesn't already carry a "Platform
  // Fee" entry (avoids double-counting).
  const { combinedTaxesAndFees, taxesAndFeesBreakdown } = (() => {
    const customFeesList = paymentSummary.custom_fees || []
    const cardFeeEntry = customFeesList.find((fee) => fee.title === "Card Processing Fee")
    const regularFees = customFeesList.filter((fee) => fee.title !== "Card Processing Fee")
    const cardFeeAmount = cardFeeEntry ? Number(cardFeeEntry.price) : 0
    const hasPlatformFee = regularFees.some((fee) => fee.title === "Platform Fee")
    const platformFeeAmount = hasPlatformFee ? 0 : Number(paymentSummary.tax_and_fee ?? 0)

    const breakdown = [...regularFees, ...(cardFeeEntry ? [cardFeeEntry] : [])]
      .filter((fee) => Number(fee.price) > 0)
      .map((fee) => ({ label: fee.title, amount: Number(fee.price) }))
    if (platformFeeAmount > 0) breakdown.push({ label: "Platform Fee", amount: platformFeeAmount })

    const total = regularFees.reduce((sum, fee) => sum + Number(fee.price ?? 0), 0) + cardFeeAmount + platformFeeAmount

    return { combinedTaxesAndFees: total, taxesAndFeesBreakdown: breakdown }
  })()

  // Add-ons are part of the subtotal (subtotal = guest lines + add-ons), so they must be
  // shown as their own lines for the breakdown to reconcile to the Subtotal/Total rows.
  const addOns = (paymentSummary.add_ons || []).map((addOn, index) => ({
    id: `addon-${index}`,
    name: addOn.name || "",
    quantity: addOn.quantity ?? 1,
    unitPrice: Number(addOn.unit_price ?? 0),
    amount: Number(addOn.total_amount ?? 0)
  }))

  return {
    reservationId: item.booking_number,
    reservationNumber: item.booking_number,
    date: item.booked_datetime,
    timeStart: item.start_time,
    timeEnd: item.end_time,
    status: item.marketplace_booking ? "Confirmed" : titleCase(item.status),
    rawStatus: item.status,
    source: item.marketplace_booking ? "Marketplace" : titleCase(item.booking_source),
    bookingSource: item.booking_source,
    created: item.booked_datetime,
    cancellationPolicy: null,

    // IDs / raw fields the reused booking-action modals need
    uuid: item.uuid,
    bookingId: item.id,
    tripUUID: trip.uuid || null,
    paymentOption: item.payment_option,
    isExpired: Boolean(item.is_expired),
    canEdit: item.can_edit ?? true,
    experienceAvatar: trip.photos || [],
    // Partial check-in/out (read-only status on the detail page)
    checkedInCount: item.checked_in_count ?? 0,
    checkedOutCount: item.checked_out_count ?? 0,
    totalGuests:
      item.total_guest_count ??
      (item.adult_count ?? 0) + (item.child_count ?? 0) + (item.infant_count ?? 0) + (item.passenger_count ?? 0),
    // Per-type breakdown. Prefer backend guest_types (carries per-type checked-in/out counts).
    guestTypes: buildGuestTypes(item.guest_types, item.total_guest_count ?? 0, item.checked_in_count ?? 0, item.checked_out_count ?? 0),

    client: {
      id: client.id,
      // ClientContact/messaging keys off a numeric user_id. The detail payload exposes it as
      // client.user_id (distinct from guide_client_id) — the record has no plain `id`, so the
      // old client.id mapping sent client_ids=NaN.
      user_id: client.user_id ?? client.id,
      // The guide-client id (distinct from the user id above) is what the Edit Client
      // modal + /guide/clients/:id endpoint need. See RESERVATION_DETAIL_API.md §2g.
      guideClientId: client.guide_client_id || null,
      name: clientName,
      first_name: client.first_name || null,
      last_name: client.last_name || null,
      phone: client.phone_number || null,
      email: client.email || null,
      avatarUrl: client.avatar || null
    },

    experience: {
      id: item.booking_number,
      title: trip.title || "",
      imageUrl: trip.photos?.[0]?.url || null,
      status: item.is_expired ? "Completed" : "Upcoming",
      date: item.booked_datetime,
      timeStart: item.start_time,
      timeEnd: item.end_time,
      adults: item.adult_count ?? 0,
      docsSign: signedWaiverCount,
      docsTotal: waivers.length,
      total: totalPrice
    },

    waivers: waivers.map((waiver) => ({
      id: waiver.booking_waiver_id,
      fullName: clientName,
      guestEmail: client.email || null,
      phone: client.phone_number || null,
      document: `Waiver #${waiver.document_id}`,
      documentUrl: waiver.document_url || null,
      age: null,
      status: waiver.is_signed ? "Signed" : "Unsigned"
    })),

    // No questionnaire answers are returned on the detail payload yet.
    answers: [],

    payments: {
      depositLabel,
      // { label, color } — same shape as the list's payment_tag/collection_tag
      paymentTag,
      collectionTag,
      isPaidInFull,
      isCollectionFailed: collectionTag?.label === "Auto-collection failed",
      isLinkSent: collectionTag?.label === "Link Sent",
      isAutoCollectEnabledDateDisplay: item.auto_collect_scheduled_at ? dayjs(item.auto_collect_scheduled_at).format("MMM D, YYYY - hh:mm A") : null,
      // FE-derived (experience start − 24h) so the enable-confirmation modal has a date even
      // when auto-collect is currently off and the backend hasn't scheduled one.
      collectionScheduleDisplay: buildCollectionSchedule(item.booked_datetime, item.start_time),
      isAutoCollectEnabledEnabled: Boolean(item.auto_collect_balance),
      // Whether the balance is still auto-chargeable. Gates the auto-collect toggle the same way
      // the old booking-details PaymentSummary does: the backend flips this to false once the
      // deposit's stored-card mandate is spent (e.g. after a manual full collection), so a later
      // refund reopening the balance does NOT resurrect the toggle. Honor it strictly — no default.
      isChargeable: Boolean(item.is_chargeable),
      isDepositOption: Boolean(item.payment_option?.includes("deposit")),
      outstandingBalance: item.outstanding_amount ?? 0,
      guestLines,
      addOns,
      // Aggregate of all fees not shown as their own line (platform + card processing, etc.)
      // so the breakdown reconciles to Total: subtotal + non_line_item_fees_total = total.
      // Subtotal row. When "Adjust Booking Price" negotiated the base down, the guest lines
      // still show the original per-type prices, so the Subtotal row carries the adjusted value
      // (original struck through) to reconcile the breakdown to Total. Mirrors the old page.
      subtotal: {
        amount: Number(paymentSummary.subtotal ?? paymentSummary.adjusted_subtotal ?? 0),
        isAdjusted: Boolean(paymentSummary.subtotal_adjusted),
        originalAmount: paymentSummary.original_subtotal != null ? Number(paymentSummary.original_subtotal) : null,
        reason: paymentSummary.subtotal_adjustment_reason || null
      },
      taxesAndFees: combinedTaxesAndFees,
      // Per-fee lines for the expandable Taxes & Fees breakdown ([] → render a plain row).
      taxesAndFeesBreakdown,
      discount: item.discount_applied && discountAmount > 0 ? { amount: discountAmount, code: item.discount_details?.code || null } : null,
      tip: tipAmount > 0 ? { amount: tipAmount } : null,
      priceAdjustments,
      total: totalPrice,
      remainingBalance,
      // Negative remaining = client overpaid (e.g. price adjusted down after payment) → refund due
      refundDue: remainingBalance < 0 ? Math.abs(remainingBalance) : 0,
      paidAmount: Math.max(totalPrice - remainingBalance, 0),
      adjustmentOutstandingBase: item.adjustment_fee_outstanding ?? paymentSummary.adjustment_fee_outstanding ?? 0,
      paymentStatus: item.payment_status,
      receiptUrl: item.receipt_url || null,
      invoiceUrl: item.booking_invoice || null,
      history: paymentHistory.map((entry, index) => ({
        id: `${entry.paid_on || "payment"}-${index}`,
        type: entry.type || "Payment",
        method: entry.method || "",
        cardLast4: entry.last4 || "",
        amount: entry.amount ?? 0,
        isRefund: (entry.type || "").toLowerCase() === "refund",
        paidOn: entry.paid_on ? dayjs(entry.paid_on).format("MM/DD/YYYY") : ""
      }))
    },

    // Communication history is not part of the detail payload yet.
    communications: []
  }
}

// SUB-RESOURCE MAPPERS (separate lazy-loaded endpoints)

// GET /trip_bookings/{uuid}/booking_waivers
// The backend groups waivers per guest, each carrying a `documents` array plus
// signed/unsigned document lists. The table renders one row per document, so we flatten
// each guest into a row per document and resolve the per-document signed state.
export const mapReservationWaiver = (waiver, guestIndex = 0) => {
  const documents = Array.isArray(waiver.documents) ? waiver.documents : []
  const signedDocuments = Array.isArray(waiver.signed_documents) ? waiver.signed_documents : []
  const unsignedDocuments = Array.isArray(waiver.unsigned_documents) ? waiver.unsigned_documents : []
  const guestStatus = waiver.status === "Signed" ? "Signed" : "Unsigned"

  const guest = {
    fullName: waiver.full_name || null,
    guestEmail: waiver.guest_email || null,
    phone: waiver.phone_number || null,
    age: waiver.age ?? null,
    guestType: waiver.type || null,
    documentUrl: waiver.document_url || null
  }

  // Per-document status falls back to the guest-level status when the signed/unsigned
  // document lists aren't populated.
  const documentStatus = (title) => {
    if (signedDocuments.includes(title)) return "Signed"
    if (unsignedDocuments.includes(title)) return "Unsigned"
    return guestStatus
  }

  if (documents.length === 0) {
    return [{ id: `waiver-${guestIndex}`, document: null, status: guestStatus, ...guest }]
  }

  return documents.map((title, docIndex) => ({
    id: `waiver-${guestIndex}-${docIndex}`,
    document: title,
    status: documentStatus(title),
    ...guest
  }))
}

// GET /guide/reservations/{uuid}/payment_history
export const mapPaymentHistoryEntry = (entry, index) => ({
  id: entry.id ?? `${entry.paid_on || "payment"}-${index}`,
  type: entry.type || "Payment",
  method: entry.method || "",
  cardLast4: entry.last4 || "",
  amount: entry.amount ?? 0,
  payerName: entry.payer_name || null,
  isRefund: (entry.type || "").toLowerCase() === "refund",
  paidOn: entry.paid_on ? dayjs(entry.paid_on).format("MM/DD/YYYY") : ""
})

// GET /guide/reservations/{uuid}/communication_history
// `mode` is the channel (sms/email → drives the icon); `type` is a human label ("Text"/
// "Email"); `title` is the message subject; `recipient`/`sent_at` are the contact + date.
// `status` flags failed sends; `sent_by` names the sender on manual messages (null = system).
export const mapCommunication = (comm, index) => ({
  id: comm.id ?? `comm-${index}`,
  type: comm.mode || "email",
  contactName: comm.title || null,
  label: comm.type || null,
  contactInfo: comm.recipient || "",
  date: comm.sent_at || "",
  status: comm.status || "sent",
  sentBy: comm.sent_by || null
})

// GET /guide/reservations/{uuid}/answers
export const mapAnswerTopic = (topic, index) => {
  const questions = Array.isArray(topic.questions) ? topic.questions : []
  return {
    id: topic.id ?? `answer-${index}`,
    topic: topic.topic || "",
    count: questions.length,
    questions: questions.map((qa) => ({ q: qa.question, a: qa.answer }))
  }
}

// REDUCER
const initialState = {
  ...LOADING_INITIAL_STATE,
  items: [],
  metadata: { ...PAGINATION_INITIAL_STATE },
  detail: {
    ...LOADING_INITIAL_STATE,
    data: null
  },
  checkIn: {
    ...LOADING_INITIAL_STATE
  },
  waivers: { ...LOADING_INITIAL_STATE, data: [], metadata: { ...PAGINATION_INITIAL_STATE } },
  paymentHistory: { ...LOADING_INITIAL_STATE, data: [], metadata: { ...PAGINATION_INITIAL_STATE } },
  communications: { ...LOADING_INITIAL_STATE, data: [], metadata: { ...PAGINATION_INITIAL_STATE } },
  answers: { ...LOADING_INITIAL_STATE, data: [] },
  paymentOption: { ...LOADING_INITIAL_STATE }
}

const reservationsReducer = createReducer(initialState, {
  [requested.type]: (state) => {
    setRequested(state)
  },
  [received.type]: (state, action) => {
    setReceived(state)
    const incoming = Array.isArray(action.payload?.items) ? action.payload.items : []
    state.items = (action.props?.append ? state.items : []).concat(incoming.map(mapReservationItem))
    Object.assign(state.metadata, action.payload?.metadata || {})
  },
  [failed.type]: (state) => {
    setFailed(state)
  },
  [requestedDetail.type]: (state) => {
    setRequested(state.detail)
  },
  [receivedDetail.type]: (state, action) => {
    setReceived(state.detail)
    state.detail.data = mapReservationDetail(action.payload)
  },
  [failedDetail.type]: (state) => {
    setFailed(state.detail)
  },
  [requestedCheckIn.type]: (state) => {
    setRequested(state.checkIn)
  },
  [receivedCheckIn.type]: (state, action) => {
    setReceived(state.checkIn)
    const updated = action.payload?.data
    if (!updated?.uuid) return
    const index = state.items.findIndex((item) => item.id === updated.uuid)
    if (index === -1) return
    if (updated.check_in_state) state.items[index].checkInState = updated.check_in_state
    if (updated.checked_in_count != null) state.items[index].checkedInCount = updated.checked_in_count
    if (updated.checked_out_count != null) state.items[index].checkedOutCount = updated.checked_out_count
    if (Array.isArray(updated.guest_types)) {
      state.items[index].guestTypes = buildGuestTypes(updated.guest_types, updated.total_guest_count ?? 0, updated.checked_in_count ?? 0, updated.checked_out_count ?? 0)
    }
  },
  [failedCheckIn.type]: (state) => {
    setFailed(state.checkIn)
  },
  [requestedWaivers.type]: (state) => {
    setRequested(state.waivers)
  },
  [receivedWaivers.type]: (state, action) => {
    setReceived(state.waivers)
    state.waivers.data = (action.payload?.data || []).flatMap((waiver, index) => mapReservationWaiver(waiver, index))
    Object.assign(state.waivers.metadata, action.payload?.metadata || {})
  },
  [failedWaivers.type]: (state) => {
    setFailed(state.waivers)
  },
  [requestedPaymentHistory.type]: (state) => {
    setRequested(state.paymentHistory)
  },
  [receivedPaymentHistory.type]: (state, action) => {
    setReceived(state.paymentHistory)
    state.paymentHistory.data = (action.payload?.data || []).map(mapPaymentHistoryEntry)
    Object.assign(state.paymentHistory.metadata, action.payload?.metadata || {})
  },
  [failedPaymentHistory.type]: (state) => {
    setFailed(state.paymentHistory)
  },
  [requestedCommunications.type]: (state) => {
    setRequested(state.communications)
  },
  [receivedCommunications.type]: (state, action) => {
    setReceived(state.communications)
    state.communications.data = (action.payload?.data || []).map(mapCommunication)
    Object.assign(state.communications.metadata, action.payload?.metadata || {})
  },
  [failedCommunications.type]: (state) => {
    setFailed(state.communications)
  },
  [requestedAnswers.type]: (state) => {
    setRequested(state.answers)
  },
  [receivedAnswers.type]: (state, action) => {
    setReceived(state.answers)
    state.answers.data = (action.payload?.data || []).map(mapAnswerTopic)
  },
  [failedAnswers.type]: (state) => {
    setFailed(state.answers)
  },
  [requestedPaymentOption.type]: (state) => {
    setRequested(state.paymentOption)
  },
  [receivedPaymentOption.type]: (state) => {
    setReceived(state.paymentOption)
  },
  [failedPaymentOption.type]: (state) => {
    setFailed(state.paymentOption)
  }
})

export default reservationsReducer

// PUBLIC ACTIONS

export const fetchReservations =
  (params, isAppend = false) =>
  (dispatch) => {
    const serializedParams = toSearchParams(params, null)
    return dispatch(
      api({
        url: "/guide/reservations",
        onStart: requested.type,
        onSuccess: received.type,
        onError: failed.type,
        params: serializedParams,
        props: { append: Boolean(isAppend) }
      })
    )
  }

export const fetchReservationDetail = (uuid) => (dispatch) =>
  dispatch(
    api({
      url: `/guide/reservations/${uuid}`,
      onStart: requestedDetail.type,
      onSuccess: receivedDetail.type,
      onError: failedDetail.type
    })
  )

// Auto-collect toggle on the detail page maps to a payment_option change. Wired with
// loading watchers so the confirmation modal's Proceed button reflects Redux loading.
export const updateReservationPaymentOption = (uuid, paymentOption) => (dispatch) =>
  dispatch(
    api({
      url: `guide/bookings/${uuid}/update_payment_option`,
      method: "PATCH",
      onStart: requestedPaymentOption.type,
      onSuccess: receivedPaymentOption.type,
      onError: failedPaymentOption.type,
      data: { trip_booking: { payment_option: paymentOption } }
    })
  )

export const patchCheckIn = (uuid, checkInState) => (dispatch) =>
  dispatch(
    api({
      url: `/guide/reservations/${uuid}/check_in`,
      method: "PATCH",
      onStart: requestedCheckIn.type,
      onSuccess: receivedCheckIn.type,
      onError: failedCheckIn.type,
      data: { state: checkInState }
    })
  )

// Partial check-in for private bookings: set the absolute checked-in count per guest type.
// `guestTypes` is [{ key, checkedInCount }]. We also send the aggregate `checked_in_count`
// so the backend keeps working until the per-type payload is consumed.
// override_documents bypasses the required-documents check (Incomplete Documents → Override).
export const checkInGuests = (uuid, guestTypes = [], { override = false } = {}) => (dispatch) =>
  dispatch(
    api({
      url: `/guide/reservations/${uuid}/check_in`,
      method: "PATCH",
      onStart: requestedCheckIn.type,
      onSuccess: receivedCheckIn.type,
      onError: failedCheckIn.type,
      data: {
        guest_types: guestTypes.map((type) => ({ key: type.key, checked_in_count: type.checkedInCount })),
        checked_in_count: guestTypes.reduce((sum, type) => sum + (type.checkedInCount ?? 0), 0),
        override_documents: override
      }
    })
  )

// Partial check-out: set the absolute checked-out count per guest type (0..that type's
// checked-in count). Aggregate `checked_out_count` is sent alongside for backward-compat.
export const checkOutGuests = (uuid, guestTypes = []) => (dispatch) =>
  dispatch(
    api({
      url: `/guide/reservations/${uuid}/check_out`,
      method: "PATCH",
      onStart: requestedCheckIn.type,
      onSuccess: receivedCheckIn.type,
      onError: failedCheckIn.type,
      data: {
        guest_types: guestTypes.map((type) => ({ key: type.key, checked_out_count: type.checkedOutCount })),
        checked_out_count: guestTypes.reduce((sum, type) => sum + (type.checkedOutCount ?? 0), 0)
      }
    })
  )

// Lazy-loaded detail sub-resources
export const fetchReservationWaivers = (uuid, params = {}) => (dispatch) =>
  dispatch(
    api({
      url: `/trip_bookings/${uuid}/booking_waivers`,
      params,
      onStart: requestedWaivers.type,
      onSuccess: receivedWaivers.type,
      onError: failedWaivers.type
    })
  )

export const fetchReservationPaymentHistory = (uuid, params = {}) => (dispatch) =>
  dispatch(
    api({
      url: `/guide/reservations/${uuid}/payment_history`,
      params,
      onStart: requestedPaymentHistory.type,
      onSuccess: receivedPaymentHistory.type,
      onError: failedPaymentHistory.type
    })
  )

// The dedicated endpoint defaults to per_page=3, so pass a large page size explicitly —
// the panel renders one scrollable, searchable list with no pagination controls.
export const fetchReservationCommunications = (uuid, params = {}) => (dispatch) =>
  dispatch(
    api({
      url: `/guide/reservations/${uuid}/communication_history`,
      params: { per_page: 50, ...params },
      onStart: requestedCommunications.type,
      onSuccess: receivedCommunications.type,
      onError: failedCommunications.type
    })
  )

export const fetchReservationAnswers = (uuid) => (dispatch) =>
  dispatch(
    api({
      url: `/guide/reservations/${uuid}/answers`,
      onStart: requestedAnswers.type,
      onSuccess: receivedAnswers.type,
      onError: failedAnswers.type
    })
  )
